import { DatePicker } from '../../../components/ui/date-picker';
// apps/web/src/pages/vendas/detalhe/editar-cabecalho-sheet.tsx — Fase 12, Plano 23 (PV-01).
// FormDialog de edição do cabeçalho do PV: PATCH /api/v1/vendas/pedidos/:id com lock otimista (`versao`).
// Conflito de versão (409 PV_VERSAO_DESATUALIZADA) NÃO apaga o que o usuário digitou: os valores ficam
// até ele clicar em "Recarregar PV", que recarrega o PV e só então reinicia o formulário.
import { useEffect, useState, type FormEvent } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { AtualizarCabecalhoPvInput, PedidoVendaView, PrioridadePv } from '@pieltec/contracts';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { FormMessage } from '../../../components/ui/form-message';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { FormDialog, FormDialogContent, FormDialogDescription, FormDialogFooter, FormDialogHeader, FormDialogTitle } from '../../../components/ui/form-dialog';
import { Textarea } from '../../../components/ui/textarea';
import { focarCampo, primeiroCampoInvalido, validarCabecalhoPv, ORDEM_CABECALHO_PV } from '../../../lib/vendas/validacao-pv';
import { usePvComandos } from '../use-pv-comandos';
import { CampoSelect } from '../../../components/ui/campo-select';

interface EditarCabecalhoSheetProps {
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
  readonly pv: PedidoVendaView;
  /** Chamado com o PV devolvido pela API após salvar. */
  readonly onSalvo: (pv: PedidoVendaView) => void;
  /** Recarrega o PV (sem esconder a tela) e devolve a versão atual, ou null se a carga falhar. */
  readonly onRecarregar: () => Promise<PedidoVendaView | null>;
  /** Falha genérica (não validação, não conflito): a página mostra o toast. */
  readonly onFalha: (mensagem: string) => void;
}

const PRIORIDADES: ReadonlyArray<{ valor: PrioridadePv; rotulo: string }> = [
  { valor: 0, rotulo: 'Normal' },
  { valor: 10, rotulo: 'Alta' },
  { valor: 20, rotulo: 'Urgente' },
];

const ID_CAMPO: Record<(typeof ORDEM_CABECALHO_PV)[number], string> = {
  clienteId: 'cab-numero-cliente',
  numeroPedidoCliente: 'cab-numero-cliente',
  data: 'cab-prazo',
  responsavelNome: 'cab-responsavel',
  condicaoComercial: 'cab-condicao',
  observacoes: 'cab-observacoes',
};

interface Campos {
  numeroPedidoCliente: string;
  prazo: string;
  prioridade: PrioridadePv;
  responsavel: string;
  condicao: string;
  observacoes: string;
}

function camposDoPv(pv: PedidoVendaView): Campos {
  return {
    numeroPedidoCliente: pv.numeroPedidoCliente ?? '',
    prazo: pv.prazoSolicitado ?? '',
    prioridade: pv.prioridade,
    responsavel: pv.responsavelNome ?? '',
    condicao: pv.condicaoComercial ?? '',
    observacoes: pv.observacoes ?? '',
  };
}

export function EditarCabecalhoSheet({ aberto, onAbertoChange, pv, onSalvo, onRecarregar, onFalha }: EditarCabecalhoSheetProps) {
  const { atualizarCabecalho } = usePvComandos();
  const [campos, setCampos] = useState<Campos>(() => camposDoPv(pv));
  const [erros, setErros] = useState<ReturnType<typeof validarCabecalhoPv>>({});
  const [conflito, setConflito] = useState<string | null>(null);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [recarregando, setRecarregando] = useState(false);

  // Reinicia só ao abrir: recarregar o PV por trás (versão nova) não pode apagar o que foi digitado.
  useEffect(() => {
    if (!aberto) return;
    setCampos(camposDoPv(pv));
    setErros({});
    setConflito(null);
    setErroEnvio(null);
  }, [aberto]);

  function alterar<K extends keyof Campos>(chave: K, valor: Campos[K]): void {
    setCampos((atuais) => ({ ...atuais, [chave]: valor }));
  }

  async function salvar(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErroEnvio(null);
    const proximos = validarCabecalhoPv({
      clienteId: pv.clienteId,
      numeroPedidoCliente: campos.numeroPedidoCliente,
      responsavelNome: campos.responsavel,
      condicaoComercial: campos.condicao,
      observacoes: campos.observacoes,
    });
    setErros(proximos);
    const invalido = primeiroCampoInvalido(proximos, ORDEM_CABECALHO_PV);
    if (invalido) {
      focarCampo(ID_CAMPO[invalido]);
      return;
    }

    const corpo: AtualizarCabecalhoPvInput = {
      versao: pv.versao,
      numeroPedidoCliente: campos.numeroPedidoCliente.trim() || null,
      prazoSolicitado: campos.prazo || null,
      prioridade: campos.prioridade,
      responsavelNome: campos.responsavel.trim() || null,
      condicaoComercial: campos.condicao.trim() || null,
      observacoes: campos.observacoes.trim() || null,
    };
    setSalvando(true);
    try {
      const resultado = await atualizarCabecalho(pv.id, corpo);
      if (resultado.tipo === 'ok') {
        setConflito(null);
        onSalvo(resultado.resultado);
        onAbertoChange(false);
      } else if (resultado.tipo === 'conflito') {
        setConflito(resultado.message);
      } else {
        setErroEnvio(resultado.message);
        onFalha(resultado.message);
      }
    } finally {
      setSalvando(false);
    }
  }

  async function recarregar(): Promise<void> {
    setRecarregando(true);
    try {
      const atual = await onRecarregar();
      if (atual) {
        setCampos(camposDoPv(atual));
        setErros({});
        setConflito(null);
        setErroEnvio(null);
      } else {
        setErroEnvio('Não foi possível recarregar o PV. Tente novamente.');
      }
    } finally {
      setRecarregando(false);
    }
  }

  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent>
        <form onSubmit={(e) => void salvar(e)} noValidate className="flex h-full flex-col">
          <FormDialogHeader>
            <FormDialogTitle className="text-xl">Editar cabeçalho</FormDialogTitle>
            <FormDialogDescription>Altere os dados do pedido {pv.numero}. Cliente e data não são editáveis.</FormDialogDescription>
          </FormDialogHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            {conflito && (
              <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                <AlertTriangle aria-hidden="true" />
                <AlertDescription className="text-amber-900">
                  <p className="font-semibold">{conflito}</p>
                  <p>O que você digitou continua aqui até você recarregar.</p>
                  <div className="mt-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => void recarregar()} disabled={recarregando}>
                      {recarregando && <Loader2 className="animate-spin" aria-hidden="true" />}
                      Recarregar PV
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cab-numero-cliente">Nº do pedido do cliente</Label>
              <Input
                id="cab-numero-cliente"
                value={campos.numeroPedidoCliente}
                onChange={(e) => alterar('numeroPedidoCliente', e.target.value)}
                aria-invalid={erros.numeroPedidoCliente ? true : undefined}
                autoFocus
              />
              {erros.numeroPedidoCliente && <FormMessage>{erros.numeroPedidoCliente}</FormMessage>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cab-prazo">Prazo solicitado</Label>
              <DatePicker id="cab-prazo" value={campos.prazo} onValueChange={(value) => alterar('prazo', value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cab-prioridade">Prioridade</Label>
              <CampoSelect
                id="cab-prioridade"
                value={String(campos.prioridade)}
                onValueChange={(valor) => alterar('prioridade', Number(valor) as PrioridadePv)}
                options={PRIORIDADES.map((p) => ({ value: String(p.valor), label: p.rotulo }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cab-responsavel">Responsável</Label>
              <Input
                id="cab-responsavel"
                value={campos.responsavel}
                onChange={(e) => alterar('responsavel', e.target.value)}
                aria-invalid={erros.responsavelNome ? true : undefined}
              />
              {erros.responsavelNome && <FormMessage>{erros.responsavelNome}</FormMessage>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cab-condicao">Condição comercial</Label>
              <Input
                id="cab-condicao"
                value={campos.condicao}
                onChange={(e) => alterar('condicao', e.target.value)}
                aria-invalid={erros.condicaoComercial ? true : undefined}
              />
              {erros.condicaoComercial && <FormMessage>{erros.condicaoComercial}</FormMessage>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="cab-observacoes">Observações</Label>
              <Textarea
                id="cab-observacoes"
                value={campos.observacoes}
                onChange={(e) => alterar('observacoes', e.target.value)}
                aria-invalid={erros.observacoes ? true : undefined}
              />
              {erros.observacoes && <FormMessage>{erros.observacoes}</FormMessage>}
            </div>
            {erroEnvio && <FormMessage>{erroEnvio}</FormMessage>}
          </div>

          <FormDialogFooter className="sm:flex-row sm:justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onAbertoChange(false)} disabled={salvando}>
              Voltar
            </Button>
            <Button type="submit" disabled={salvando}>
              Salvar cabeçalho
            </Button>
          </FormDialogFooter>
        </form>
      </FormDialogContent>
    </FormDialog>
  );
}
